'use client';

import React from 'react';

/**
 * Fuhriva meeting landing page — locked down.
 * No self-service room creation. Participants join exclusively via
 * Fuhriva-signed links (/custom/?liveKitUrl=...&token=...).
 * This page is purely informational.
 */

function Logo() {
  return (
    <div className="fv-logo">
      <img src="/images/Logo_Full_Color_Dark.svg" alt="Fuhriva" height="34" />
    </div>
  );
}

export default function Page() {
  return (
    <main className="fv-main">
      <div className="fv-bg" aria-hidden="true" />

      <header className="fv-header">
        <Logo />
        <span className="fv-badge">
          <span className="fv-badge-dot" />
          Sicher · verschlüsselt
        </span>
      </header>

      <section className="fv-stage">
        <div className="fv-card">
          <span className="fv-eyebrow">FUHRIVA · VIDEOBERATUNG</span>
          <h1 className="fv-title">Willkommen</h1>
          <p className="fv-sub">
            Dieser Bereich ist ausschließlich über Ihren persönlichen Einladungslink erreichbar.
            Bitte öffnen Sie den Link aus Ihrer Terminbestätigung, um der Beratung beizutreten.
          </p>
          <div className="fv-hint">
            <span className="fv-hint-icon" aria-hidden="true">
              ℹ
            </span>
            <span>
              Kein Link erhalten? Wenden Sie sich an Ihre Ansprechpartnerin oder Ihren
              Ansprechpartner bei Fuhriva.
            </span>
          </div>
        </div>
      </section>

      <footer className="fv-footer">
        Fuhriva · Fuhrpark-Management-Plattform · Videoberatung
      </footer>

      <style jsx global>{`
        :root {
          --fv-100: #d2f5e3;
          --fv-400: #39c389;
          --fv-500: #27a06d;
          --fv-600: #1f8159;
          --fv-900: #123f2f;
          --fv-ink-900: #0f1512;
          --fv-ink-500: #6b7671;
          --fv-radius-lg: 16px;
          --fv-radius-sm: 8px;
        }
        html,
        body {
          margin: 0;
          padding: 0;
          background: var(--fv-900);
          font-family: 'Geist', ui-sans-serif, system-ui, -apple-system, sans-serif;
        }
        .fv-main {
          position: relative;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          color: #fff;
          overflow: hidden;
        }
        .fv-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          background:
            radial-gradient(circle at 22% 18%, rgba(57, 195, 137, 0.22), transparent 42%),
            radial-gradient(circle at 82% 82%, rgba(31, 129, 89, 0.28), transparent 46%),
            linear-gradient(160deg, #123f2f 0%, #0f3327 60%, #0c281f 100%);
        }
        .fv-bg::after {
          content: '';
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px);
          background-size: 26px 26px;
          mask-image: radial-gradient(circle at 50% 40%, #000 30%, transparent 78%);
        }
        .fv-header,
        .fv-stage,
        .fv-footer {
          position: relative;
          z-index: 1;
        }
        .fv-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 28px clamp(20px, 5vw, 56px);
        }
        .fv-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .fv-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: var(--fv-100);
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          padding: 7px 14px;
          border-radius: 999px;
        }
        .fv-badge-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--fv-400);
          box-shadow: 0 0 0 4px rgba(57, 195, 137, 0.2);
        }
        .fv-stage {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        .fv-card {
          width: 100%;
          max-width: 460px;
          background: #fff;
          color: var(--fv-ink-900);
          border-radius: var(--fv-radius-lg);
          padding: clamp(28px, 4vw, 40px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.6) inset,
            0 24px 60px -18px rgba(0, 0, 0, 0.55),
            0 2px 8px rgba(0, 0, 0, 0.18);
          animation: fv-rise 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .fv-eyebrow {
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 11px;
          letter-spacing: 0.14em;
          color: var(--fv-500);
        }
        .fv-title {
          margin: 10px 0 8px;
          font-size: clamp(26px, 4vw, 32px);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--fv-900);
        }
        .fv-sub {
          margin: 0;
          font-size: 15px;
          line-height: 1.55;
          color: var(--fv-ink-500);
        }
        .fv-hint {
          margin-top: 22px;
          padding-top: 20px;
          border-top: 1px solid #eef2f0;
          display: flex;
          gap: 10px;
          align-items: flex-start;
          font-size: 13.5px;
          line-height: 1.5;
          color: var(--fv-ink-500);
        }
        .fv-hint-icon {
          flex: none;
          width: 20px;
          height: 20px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          background: var(--fv-100);
          color: var(--fv-600);
          font-size: 12px;
          font-weight: 700;
        }
        .fv-footer {
          text-align: center;
          padding: 22px;
          font-size: 12.5px;
          color: rgba(255, 255, 255, 0.5);
        }
        @keyframes fv-rise {
          from {
            opacity: 0;
            transform: translateY(14px) scale(0.985);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </main>
  );
}
